import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

/**
 * Resolves short link tokens to full incident paths.
 * Route: /i/:token
 * Reads short_links/{token} from Firestore and redirects to /incidents/{connectionId}/{conversationId}/{incidentId}
 */
const ShortLinkResolver = () => {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (authLoading || !user || !token) return;

    const resolve = async () => {
      try {
        const snap = await getDoc(doc(db, "short_links", token));
        if (snap.exists()) {
          const data = snap.data();
          setRedirectPath(`/incidents/${data.connectionId}/${data.conversationId}/${data.incidentId}`);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error("Failed to resolve short link:", err);
        setError(true);
      }
    };

    resolve();
  }, [token, user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return <Navigate to="/incidents" replace />;
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <span className="ml-2 text-muted-foreground">Loading incident...</span>
    </div>
  );
};

export default ShortLinkResolver;
